'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Grade } from 'ts-fsrs'
import { FSRSScheduler } from '@/lib/fsrs/scheduler'
import { CollectionEntity } from '@/lib/fsrs/types'
import { invalidateCardQueries } from '@/lib/fsrs/invalidate-queries'
import type { FSRSParameters } from 'ts-fsrs'
import { type SessionCard } from '@/lib/fsrs/fetch-session'
import { Button } from '@/components/ui/button'
import { SelectableText } from '@/components/details/selectable-text'
import { useAudio } from '@/hooks/useAudio'
import { useAuth } from '@/hooks/useAuth'
import { useSettings } from '@/hooks/useSettings'
import { IconVolume } from '@tabler/icons-react'
import { submitStudyReview } from '@/lib/study'

type Props = {
  initialCards: SessionCard[]
  collection?: CollectionEntity | null // Collection entity (for fsrs_params)
  collectionId?: string | null // Collection context for logging
  curatedSlug?: string | null // For curated collections
  onComplete?: () => void
  onQueueChange?: (payload: { remaining: number; total: number }) => void
}

const REINSERT_OFFSET = 3
const IN_SESSION_MINUTES = 15

export default function FlashcardRunner({
  initialCards,
  collection,
  collectionId,
  curatedSlug,
  onComplete,
  onQueueChange
}: Props) {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const { settings } = useSettings()
  const [queue, setQueue] = useState<SessionCard[]>(initialCards)
  const [showAnswer, setShowAnswer] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const total = initialCards.length

  // Determine script type from user settings (consistent with other components)
  const scriptType: 'simplified' | 'traditional' =
    settings.scriptDisplay === 'traditional' ? 'traditional' : 'simplified'
  const { play: playAudio, isPlaying: isAudioPlaying } = useAudio(scriptType)

  useEffect(() => {
    if (queue.length === 0 && onComplete) onComplete()
  }, [queue.length, onComplete])

  useEffect(() => {
    if (onQueueChange) {
      onQueueChange({ remaining: queue.length, total })
    }
  }, [onQueueChange, queue.length, total])

  const fsrsParams = useMemo(
    () =>
      (collection?.fsrs_params as FSRSParameters | null | undefined) ?? null,
    [collection?.fsrs_params]
  )

  const scheduler = useMemo(() => new FSRSScheduler(fsrsParams), [fsrsParams])

  const currentCard = queue[0]
  const nextStates = currentCard ? scheduler.getRecordLog(currentCard) : null

  const handleRate = useCallback(
    async (rating: Grade) => {
      if (!currentCard || submitting) return
      setSubmitting(true)
      setError(null)

      const now = new Date()
      const { cardUpdate } = scheduler.processReview(currentCard, rating, now)

      setQueue((prev) => {
        const remainingQueue = prev.slice(1)
        const updatedCard = { ...currentCard, ...cardUpdate }
        const nextIntervalMinutes =
          (new Date(cardUpdate.due).getTime() - now.getTime()) / 60000

        if (nextIntervalMinutes <= IN_SESSION_MINUTES) {
          const insertIndex = Math.min(remainingQueue.length, REINSERT_OFFSET)
          const newQueue = [...remainingQueue]
          newQueue.splice(insertIndex, 0, updatedCard)
          return newQueue
        }

        return remainingQueue
      })

      setShowAnswer(false)

      try {
        await submitStudyReview({
          cardId: currentCard.id,
          rating,
          deckId: curatedSlug ? null : (collectionId ?? null),
          curatedSlug: curatedSlug ?? null
        })
        if (user?.id) {
          invalidateCardQueries(queryClient, user.id)
        }
      } catch (reviewError) {
        setError(
          reviewError instanceof Error
            ? reviewError.message
            : 'Failed to submit review'
        )
        // Put the card back on top so the user can try again
        setQueue((prev) => {
          const alreadyQueued = prev.some((c) => c.id === currentCard.id)
          return alreadyQueued ? prev : [currentCard, ...prev]
        })
      }

      setSubmitting(false)
    },
    [
      currentCard,
      scheduler,
      submitting,
      queryClient,
      user?.id,
      collectionId,
      curatedSlug
    ]
  )

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null
      if (
        target &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')
      )
        return
      if (!currentCard) return

      if (event.code === 'Space') {
        event.preventDefault()
        if (!showAnswer) {
          setShowAnswer(true)
        }
        return
      }

      if (!showAnswer) return

      const keyMap: Record<string, Grade> = {
        Digit1: 1,
        Digit2: 2,
        Digit3: 3,
        Digit4: 4
      }

      const rating = keyMap[event.code]
      if (rating) {
        event.preventDefault()
        handleRate(rating)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [handleRate, currentCard, showAnswer])

  const note = currentCard?.note
  const fields = note?.fields || {}
  const isWordCard = note?.note_type === 'word'
  const rawTones = fields.tones
  const tones: number[] =
    Array.isArray(rawTones) &&
    rawTones.every((tone): tone is number => typeof tone === 'number')
      ? rawTones
      : []
  const pinyin = typeof fields.pinyin === 'string' ? fields.pinyin : ''
  const zhuyin = typeof fields.zhuyin === 'string' ? fields.zhuyin : ''
  const displayFront = currentCard?.front
  const canPlayFrontAudio =
    typeof displayFront === 'string' && /[\u3400-\u9fff]/.test(displayFront)
  const sentenceSource =
    note?.note_type === 'sentence' && typeof note.source?.title === 'string'
      ? note.source.title
      : null

  // Determine which pronunciations to show based on settings
  const showPinyin = settings.pronunciationDisplay !== 'zhuyin'
  const showZhuyin = settings.pronunciationDisplay !== 'pinyin'
  const canColor =
    isWordCard &&
    Array.isArray(tones) &&
    typeof displayFront === 'string' &&
    tones.length === displayFront.length

  // Autoplay audio when card is revealed
  useEffect(() => {
    if (showAnswer && settings.flashcardAutoplayAudio && canPlayFrontAudio) {
      playAudio(displayFront)
    }
    // Only trigger on showAnswer change, not on displayFront changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAnswer])

  if (!currentCard) {
    return null
    return (
      <div className="text-muted-foreground bg-muted w-full rounded-lg p-6 text-center text-sm shadow-sm">
        No cards to review.
      </div>
    )
  }

  return (
    <div className="flex w-full max-w-3xl flex-col gap-6">
      <div className="bg-muted/50 relative rounded-lg p-6 shadow-md">
        <div className="text-muted-foreground mb-4 flex items-center justify-between text-xs">
          <span className="font-medium">
            {total - queue.length + 1} / {total || 1}
          </span>
          {canPlayFrontAudio && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              disabled={isAudioPlaying}
              onClick={() => playAudio(displayFront)}
              title="Play audio"
            >
              <IconVolume
                className={`h-4 w-4 ${isAudioPlaying ? 'animate-pulse' : ''}`}
              />
            </Button>
          )}
        </div>

        <div className="flex flex-col items-center gap-6">
          <div className="text-foreground bg-muted relative min-h-[240px] w-full rounded-lg px-6 py-8 text-center text-3xl leading-relaxed shadow-inner sm:text-4xl">
            <div className="flex flex-col items-center gap-2">
              {canColor && typeof displayFront === 'string' ? (
                <SelectableText text={displayFront} tones={tones} size="xl" />
              ) : (
                <div className="text-6xl sm:text-7xl">{displayFront}</div>
              )}
              {showAnswer && (pinyin || zhuyin) && (
                <div className="text-muted-foreground flex items-center gap-3 text-lg sm:text-xl">
                  {showPinyin && pinyin && <span>{pinyin}</span>}
                  {showZhuyin && zhuyin && (
                    <span className="text-base sm:text-lg">{zhuyin}</span>
                  )}
                </div>
              )}
            </div>
          </div>

          {showAnswer && (
            <div className="text-foreground bg-muted/60 w-full rounded-lg px-6 py-4 text-center text-lg shadow-sm sm:text-xl">
              <div className="whitespace-pre-line">{currentCard.back}</div>
              {sentenceSource && (
                <div className="text-muted-foreground mt-3 text-xs">
                  From {sentenceSource}
                </div>
              )}
            </div>
          )}
        </div>

        {!showAnswer ? (
          <div className="mt-6 flex justify-center">
            <Button
              variant="outline"
              size="default"
              onClick={() => setShowAnswer(true)}
            >
              Show answer
            </Button>
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-4 gap-3">
            {[
              {
                rating: 1 as Grade,
                label: 'Again',
                color:
                  'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900/50'
              },
              {
                rating: 2 as Grade,
                label: 'Hard',
                color:
                  'bg-orange-50 dark:bg-orange-950/30 border-orange-200 dark:border-orange-900/50'
              },
              {
                rating: 3 as Grade,
                label: 'Good',
                color:
                  'bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-900/50'
              },
              {
                rating: 4 as Grade,
                label: 'Easy',
                color:
                  'bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-900/50'
              }
            ].map((btn) => (
              <Button
                key={btn.rating}
                disabled={!nextStates || submitting}
                variant="outline"
                className={`h-auto flex-col items-start py-3 text-sm transition hover:opacity-80 ${btn.color} ${submitting ? 'opacity-70' : ''}`}
                onClick={() => handleRate(btn.rating)}
              >
                <div className="font-medium">{btn.label}</div>
                <div className="text-muted-foreground text-xs">
                  {nextStates
                    ? scheduler.formatDueLabel(
                        nextStates[btn.rating].card.due,
                        new Date()
                      )
                    : '--'}
                </div>
              </Button>
            ))}
          </div>
        )}

        <div className="text-muted-foreground mt-4 text-center text-xs">
          Space to toggle answer • 1/2/3/4 to rate
        </div>
      </div>

      {error && <div className="text-destructive text-sm">{error}</div>}
    </div>
  )
}
